import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { useDispatch } from 'react-redux';
import { store, persistor } from './redux/store';
import { checkAuthSession } from './redux/slices/userSlice';
import { AppDispatch } from './redux/store';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './services/firebaseConfig';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './pages/Home/Home';
import SignIn from './pages/SignIn/SignIn';
import ResetPasswordRequest from './pages/Auth/ResetPasswordRequest';
import PasswordReset from './pages/Auth/PasswordReset';
import Problems from './pages/Problems/Problems';
import ChatPage from './pages/ChatPage/ChatPage';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import OverviewView from './pages/dashboard/views/OverviewView';
import TrendsView from './pages/dashboard/views/TrendsView';
import OrganizationsListView from './pages/dashboard/views/OrganizationsListView';
import OrganizationDetailView from './pages/dashboard/views/OrganizationDetailView';
import UsersListView from './pages/dashboard/views/UsersListView';
import UserDetailView from './pages/dashboard/views/UserDetailView';
import CreateUserView from './pages/dashboard/views/CreateUserView';
import BillingView from './pages/dashboard/views/BillingView';
import OrganizationBillingDetailView from './pages/dashboard/views/OrganizationBillingDetailView';
import ConversationDetailFlowView from './pages/dashboard/views/ConversationDetailFlowView';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import { theme } from './styles/theme';
import MenuOverrides from './components/chat/MenuOverrides';
import I18nProvider from './components/I18nProvider/I18nProvider';

// AuthInitializer component to handle Firebase authentication state changes
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    console.log('🔧 Setting up Firebase auth state listener...');
    
    // Set up Firebase auth state listener
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔄 Firebase auth state changed:', firebaseUser ? 'User found' : 'No user');
      
      try {
        // Dispatch checkAuthSession to sync Redux state with Firebase auth state
        await dispatch(checkAuthSession());
        console.log('✅ Auth session check completed');
      } catch (error) {
        console.error('❌ Auth session check failed:', error);
      } finally {
        // Mark auth as initialized regardless of success/failure
        setAuthInitialized(true);
        console.log('✅ Auth initialization completed');
      }
    });

    // Cleanup listener on unmount
    return () => {
      console.log('🧹 Cleaning up Firebase auth listener');
      unsubscribe();
    };
  }, [dispatch]);

  // Show loading spinner while waiting for auth to initialize
  if (!authInitialized) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        bgcolor={theme.colors.bone}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MenuOverrides />
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <I18nProvider>
              <AuthInitializer>
                <Router>
                  <Routes>
                    <Route path="/" element={<StandardLayout><Navigate to="/chat" replace /></StandardLayout>} />
                    <Route path="/signin" element={<StandardLayout><SignIn /></StandardLayout>} />
                    <Route path="/reset-password-request" element={<StandardLayout><ResetPasswordRequest /></StandardLayout>} />
                    <Route path="/reset-password" element={<StandardLayout><PasswordReset /></StandardLayout>} />
                    <Route path="/problems" element={<StandardLayout><Problems /></StandardLayout>} />
                    <Route path="/chat" element={<StandardLayout><ChatPage /></StandardLayout>} />
                    <Route path="/dashboard" element={<StandardLayout><DashboardLayout /></StandardLayout>}>
                      <Route index element={<OverviewView />} />
                      <Route path="trends" element={<TrendsView />} />
                      <Route path="organizations" element={<OrganizationsListView />} />
                      <Route path="organizations/:orgName" element={<OrganizationDetailView />} />
                      <Route path="users" element={<UsersListView />} />
                      <Route path="users/create" element={<CreateUserView />} />
                      <Route path="users/:userId" element={<UserDetailView />} />
                      <Route path="billing" element={<BillingView />} />
                      <Route path="billing/:orgName" element={<OrganizationBillingDetailView />} />
                      <Route 
                        path="users/:userId/conversations/:conversationId/:conversationType"
                        element={<ConversationDetailFlowView />}
                      />
                    </Route>
                  </Routes>
                </Router>
              </AuthInitializer>
            </I18nProvider>
          </PersistGate>
        </Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const StandardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="app">
    <Header />
    <main className="main-content">{children}</main>
    <Footer />
  </div>
);

export default App;
